import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Upload, ArrowRight, CheckCircle, AlertCircle, Sparkles, ChevronLeft } from 'lucide-react';

interface UploadFormData {
  height: number;
  weight: number;
  size: string;
  gender: string;
  bodyShape: string;
}

interface UploadFlowProps {
  onComplete: (data: UploadFormData) => void;
}

const UploadFlow: React.FC<UploadFlowProps> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState<UploadFormData>({
    height: 0,
    weight: 0,
    size: '',
    gender: '',
    bodyShape: ''
  });

  const handleFileUpload = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    setUploadStatus('uploading');
    
    // Simulate upload process
    setTimeout(() => {
       setUploadStatus('success');
       setTimeout(() => {
         setCurrentStep(2);
         setUploadStatus('idle');
       }, 1000);
    }, 2000);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleFileUpload(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentStep(3);
    
    // Simulate processing
    setTimeout(() => {
      onComplete(formData);
    }, 3000);
  };

  const renderStep1 = () => (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-12"
      >
        <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-full text-sm font-semibold mb-8 shadow-lg">
          <Sparkles className="w-4 h-4" />
          <span>Step 1 of 3</span>
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Upload Your Photos</h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">Take front and side photos for instant 3D body modeling</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="bg-white rounded-3xl p-12 shadow-xl border border-gray-100"
      >
        <div
          className={`upload-area ${uploadStatus !== 'idle' ? `has-files ${uploadStatus}` : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {uploadStatus === 'idle' && (
            <>
              <div className="text-8xl mb-8">📸</div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Drop your photos here</h3>
              <p className="text-xl text-gray-600 mb-8">or click to browse</p>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => handleFileUpload(e.target.files)}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white text-lg px-8 py-4 rounded-2xl font-semibold inline-flex items-center gap-3 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 cursor-pointer">
                <Upload className="w-5 h-5" />
                Choose Files
              </label>
            </>
          )}

          {uploadStatus === 'uploading' && (
            <>
              <div className="spinner mb-8"></div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Uploading...</h3>
              <p className="text-xl text-gray-600">Processing your photos</p>
            </>
          )}

          {uploadStatus === 'success' && (
            <>
              <CheckCircle className="w-20 h-20 text-green-500 mb-8" />
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Upload Complete!</h3>
              <p className="text-xl text-gray-600">Photos processed successfully</p>
            </>
          )}

          {uploadStatus === 'error' && (
            <>
              <AlertCircle className="w-20 h-20 text-red-500 mb-8" />
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Upload Failed</h3>
              <p className="text-xl text-gray-600">Please try again</p>
            </>
          )}
        </div>

        <div className="mt-12 p-8 bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl">
          <h4 className="font-bold text-blue-900 mb-6 flex items-center text-xl">
            <span className="text-2xl mr-3">💡</span>
            Pro Tips for Best Results
          </h4>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <span className="text-xl">👕</span>
                <span className="text-gray-800 font-medium">Wear tight-fitting clothing (not loose)</span>
              </div>
              <div className="flex items-start space-x-3">
                <span className="text-xl">🧍</span>
                <span className="text-gray-800 font-medium">Stand straight with arms slightly away from body</span>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <span className="text-xl">📱</span>
                <span className="text-gray-800 font-medium">Take photos from front and side angles</span>
              </div>
              <div className="flex items-start space-x-3">
                <span className="text-xl">💡</span>
                <span className="text-gray-800 font-medium">Ensure good lighting and clear background</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );

  const renderStep2 = () => (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-12"
      >
        <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-green-600 to-blue-600 text-white px-6 py-3 rounded-full text-sm font-semibold mb-8 shadow-lg">
          <CheckCircle className="w-4 h-4" />
          <span>Step 2 of 3</span>
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Your Information</h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">Help us provide more accurate measurements</p>
      </motion.div>

      <motion.form
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        onSubmit={onSubmit}
        className="bg-white rounded-3xl p-12 shadow-xl border border-gray-100"
      >
        <div className="grid md:grid-cols-2 gap-8 mb-8">
           <div>
             <label className="block text-lg font-bold text-gray-900 mb-4 flex items-center">
               <span className="text-2xl mr-3">📏</span>
               Height
             </label>
             <input
               type="number"
               placeholder="180"
               className="w-full px-6 py-4 border-2 border-gray-300 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-lg text-gray-900 placeholder-gray-500"
               value={formData.height || ''}
               onChange={(e) => setFormData({ ...formData, height: Number(e.target.value) })}
               required
             />
             <span className="text-sm text-gray-500 mt-2 block">cm</span>
           </div>

           <div>
             <label className="block text-lg font-bold text-gray-900 mb-4 flex items-center">
               <span className="text-2xl mr-3">⚖️</span>
               Weight
             </label>
             <input
               type="number"
               placeholder="75"
               className="w-full px-6 py-4 border-2 border-gray-300 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-lg text-gray-900 placeholder-gray-500"
               value={formData.weight || ''}
               onChange={(e) => setFormData({ ...formData, weight: Number(e.target.value) })}
               required
             />
             <span className="text-sm text-gray-500 mt-2 block">kg</span>
           </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
           <div>
             <label className="block text-lg font-bold text-gray-900 mb-4 flex items-center">
               <span className="text-2xl mr-3">👕</span>
               Usual Size
             </label>
             <select
               className="w-full px-6 py-4 border-2 border-gray-300 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-lg text-gray-900"
               value={formData.size}
               onChange={(e) => setFormData({ ...formData, size: e.target.value })}
               required
             >
               <option value="">Select size</option>
               <option value="XS">XS</option>
               <option value="S">S</option>
               <option value="M">M</option>
               <option value="L">L</option>
               <option value="XL">XL</option>
               <option value="XXL">XXL</option>
             </select>
           </div>

           <div>
             <label className="block text-lg font-bold text-gray-900 mb-4 flex items-center">
               <span className="text-2xl mr-3">👤</span>
               Gender
             </label>
             <select
               className="w-full px-6 py-4 border-2 border-gray-300 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-lg text-gray-900"
               value={formData.gender}
               onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
               required
             >
               <option value="">Select gender</option>
               <option value="male">Male</option>
               <option value="female">Female</option>
               <option value="other">Other</option>
             </select>
           </div>

           <div>
             <label className="block text-lg font-bold text-gray-900 mb-4 flex items-center">
               <span className="text-2xl mr-3">🏃</span>
               Body Shape
             </label>
             <select
               className="w-full px-6 py-4 border-2 border-gray-300 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-lg text-gray-900"
               value={formData.bodyShape}
               onChange={(e) => setFormData({ ...formData, bodyShape: e.target.value })}
               required
             >
               <option value="">Select shape</option>
               <option value="athletic">Athletic</option>
               <option value="slim">Slim</option>
               <option value="regular">Regular</option>
               <option value="plus">Plus</option>
             </select>
           </div>
        </div>

        <div className="flex justify-between items-center">
          <button
            type="button"
            onClick={() => setCurrentStep(1)}
            className="flex items-center space-x-2 px-6 py-4 border-2 border-gray-300 text-gray-700 hover:border-blue-500 hover:text-blue-600 font-semibold rounded-2xl transition-all duration-200"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <button type="submit" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white text-lg px-8 py-4 rounded-2xl font-semibold inline-flex items-center gap-3 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
            Continue
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </motion.form>
    </div>
  );

  const renderStep3 = () => (
    <div className="max-w-4xl mx-auto text-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        className="bg-white rounded-3xl p-12 shadow-xl border border-gray-100"
      >
        <div className="text-8xl mb-8">🤖</div>
        <div className="spinner mx-auto mb-8"></div>
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Processing Your Measurements</h1>
        <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto leading-relaxed">Our AI is analyzing your photos and generating precise measurements</p>
        
        <div className="space-y-6 text-left max-w-md mx-auto">
          <div className="flex items-center space-x-4">
            <CheckCircle className="w-6 h-6 text-green-500" />
            <span className="text-lg text-gray-700 font-medium">Photo analysis complete</span>
          </div>
          <div className="flex items-center space-x-4">
            <CheckCircle className="w-6 h-6 text-green-500" />
            <span className="text-lg text-gray-700 font-medium">3D model generated</span>
          </div>
          <div className="flex items-center space-x-4">
            <div className="spinner w-6 h-6"></div>
            <span className="text-lg text-gray-700 font-medium">Calculating measurements...</span>
          </div>
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
      </div>
    </div>
  );
};

export default UploadFlow; 